// libs
import React, { memo, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';

// global components
import Carousel from 'shared/components/Carousel';
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// components
import DesktopImageZoom from './components/DesktopImageZoom';
import VideoPlayer from './components/VideoPlayer';
import VideoPlayerPlaceholder from './components/VideoPlayer/Placeholder';
import Img from 'shared/components/ImageV2';

// hooks
import {
  useSelectedSKUMedias,
  useSelectedSKUSubTitle,
  useSelectedSKUTitle,
} from 'shared/store/details/hooks/selectors';

// helpers
import { getOptimisedImageUrl } from 'shared/helpers/utils';
import { getImageWidth, getResponsiveImageHeight } from './helpers';
//helpers
import {
  saveImageData,
  getImageMeta,
} from 'shared/layouts/DetailsPage/components/ProductImage/helpers';

// constants
import { BREAKPOINTS } from 'shared/styles/base';
import { NAV_STATES, PLACEHOLDER_IMAGE_WIDTH } from './constants';

// styles
import { Placeholder, ImageContent } from './styles';

// defs
interface IImageProps extends RouteComponentProps<{}, {}> {
  selectedImage: number;
  setShowViewSimilar?: (value: boolean) => void;
  productId: string;
}

const EMPTY_FUNC = () => {
  /** DO NOTHING */
};

function Image({
  selectedImage,
  history,
  productId,
  setShowViewSimilar = EMPTY_FUNC,
}: IImageProps) {
  const [dotImage, setDotImage] = useState(selectedImage);
  const [showCarousel, setShowCarousel] = useState(false);
  const [slideWidth, setSlideWidth] = useState(0);
  const [responsiveHeight, setResponsiveHeight] = useState(0);
  const images = useSelectedSKUMedias();
  const productTitle = useSelectedSKUTitle();
  const productSubTitle = useSelectedSKUSubTitle();
  const containerRef = useRef<HTMLDivElement>(null);

  const { deviceWidth } = useResponsiveContext();
  const isMobile = deviceWidth <= BREAKPOINTS.MOBILE;

  const onUnload = useCallback(() => {
    saveImageData(dotImage, images.length, productId);
  }, [dotImage, images.length, productId]);

  useEffect(() => {
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [onUnload]);

  useEffect(() => {
    return () => {
      saveImageData(dotImage, images.length, productId);
    };
  }, [dotImage, images.length, productId]);

  useEffect(() => {
    if (history.location.state !== undefined) {
      if (history.location.state[NAV_STATES.IMAGE_ZOOMED] !== undefined) {
        history.goBack();
      }
      if (history.location.state[NAV_STATES.IMAGE_ZOOM_INIT] !== undefined) {
        history.goBack();
      }
    }
  }, [history]);

  useEffect(() => {
    const newWidth = getImageWidth(deviceWidth);
    if (newWidth === slideWidth) {
      return;
    }

    setSlideWidth(newWidth);
    // calculate image height based on a screen height to image ratio
    let originalSlideHeight = newWidth / images[0].aspect_ratio;
    setResponsiveHeight(getResponsiveImageHeight(originalSlideHeight, 0.75, isMobile));

    if (showCarousel === false) {
      setShowCarousel(true);
    }
  }, [
    deviceWidth,
    slideWidth,
    setSlideWidth,
    showCarousel,
    setShowCarousel,
    images,
    isMobile,
    responsiveHeight,
  ]);

  const videoIndexes = useMemo(() => {
    const indexes: number[] = [];
    images.forEach((img, idx) => {
      if (img.mediaType === 'video') {
        indexes.push(idx);
      }
    });

    return indexes;
  }, [images]);

  useEffect(() => {
    if (videoIndexes.includes(dotImage)) {
      setShowViewSimilar(false);
    } else {
      setShowViewSimilar(true);
    }
  }, [dotImage, setShowViewSimilar, videoIndexes]);

  if (showCarousel === false) {
    return (
      <Placeholder
        backgroundImage={getOptimisedImageUrl(images[0].url, {
          width: PLACEHOLDER_IMAGE_WIDTH,
        })}
      />
    );
  }

  let slideHeight = slideWidth / images[0].aspect_ratio;
  let imageWidth = slideWidth;
  const selectedImageSrc = images[selectedImage].url;

  if (isMobile && responsiveHeight > 0) {
    slideHeight = responsiveHeight;
    imageWidth = responsiveHeight * images[0].aspect_ratio;
  }

  const { titleText } = getImageMeta(productTitle, productSubTitle);

  return (
    <>
      <ImageContent ref={containerRef} data-test="product-mobile-image">
        <Carousel
          slideWidth={slideWidth}
          slideHeight={slideHeight}
          currentSlide={selectedImage}
          onChange={setDotImage}
          showNav={false}
        >
          {images.map((img, idx) => {
            const altText = titleText + ' ' + (idx + 1);
            return img.mediaType === 'video' ? (
              <VideoPlayer
                width={slideWidth}
                height={slideHeight}
                src={img.url}
                videoId={img.id}
                key={`carousel-video-${img.id || idx}`}
                videoIndex={idx}
                poster={getOptimisedImageUrl(img.thumbnailUrl, { width: slideWidth })}
                fallback={
                  <VideoPlayerPlaceholder
                    width={slideWidth}
                    height={slideHeight}
                    poster={getOptimisedImageUrl(img.thumbnailUrl, {
                      width: slideWidth,
                    })}
                  />
                }
              />
            ) : (
              <div style={{ width: slideWidth, textAlign: 'center' }} key={`carousel-img-${img.id || idx}`}>
                <Img
                  src={img.url}
                  width={imageWidth}
                  height={slideHeight}
                  alt={altText}
                  title={altText}
                  lazy={idx !== 0}
                />
              </div>
            );
          })}
        </Carousel>
      </ImageContent>

      {images[selectedImage].mediaType !== 'video' && (
        <DesktopImageZoom zoomElRef={containerRef} imageSrc={selectedImageSrc} />
      )}
    </>
  );
}

export default withRouter(memo(Image));
